// ルート描画用のキャンバス描画ユーティリティ。
// fork_webapp_react の drawUtil.ts から、シナリオ編集のプレビューに必要な範囲
// （背景画像・グリッド・スロット・ルート）だけを移植したもの。
// ロボット現在位置・障害物・反射ポール・マーク位置などのセンサ描画は含まない。

import type {
  RouteMap,
  MapCanvasParams,
  RouteWithAction,
  Pose,
  Station,
} from '@/types'
import { buildRoutePoints } from './mapUtil'
import { computeLoadedFlags, isResumeWaitStep, isWaitStep } from './routeUtil'
import { ENABLE_CARGO_TOGGLE, SLOT_TYPE_CARGO } from '@/config/scenarioConfig'

// 「荷」バッジの色（青縁・青文字）
export const LOADED_BADGE_COLOR = '#3b82f6'

/**
 * ポリラインの経路長のちょうど半分の位置（中間点）を求める
 */
export function getPathMidPoint(points: { x: number; y: number }[]): {
  x: number
  y: number
} {
  let totalLength = 0
  for (let i = 1; i < points.length; i++) {
    const dx = points[i].x - points[i - 1].x
    const dy = points[i].y - points[i - 1].y
    totalLength += Math.sqrt(dx * dx + dy * dy)
  }
  const halfLength = totalLength / 2
  let acc = 0
  for (let i = 1; i < points.length; i++) {
    const dx = points[i].x - points[i - 1].x
    const dy = points[i].y - points[i - 1].y
    const segLen = Math.sqrt(dx * dx + dy * dy)
    if (acc + segLen >= halfLength) {
      const t = segLen > 0 ? (halfLength - acc) / segLen : 0
      return {
        x: points[i - 1].x + dx * t,
        y: points[i - 1].y + dy * t,
      }
    }
    acc += segLen
  }
  return points[Math.floor(points.length / 2)]
}

interface RouteDrawOptions {
  routeIndex: number
  color: string
  isHighlighted?: boolean
  showActionLabel?: boolean
  displayIndex?: number
  showIntermediatePoints?: boolean
  showSlots?: boolean
  showArrow?: boolean
  lineWidth?: number
  slotStyle?: 'default' | 'red-circle'
  isLoaded?: boolean // 荷物を持って走行するルート（パス中間に「荷」バッジを表示）
}

// 単一点（スロット）を描画
function drawSinglePoint(
  ctx: CanvasRenderingContext2D,
  pos: { x: number; y: number },
  options: RouteDrawOptions
): void {
  const { routeIndex, color, slotStyle = 'default' } = options

  if (slotStyle === 'red-circle') {
    ctx.fillStyle = 'red'
    ctx.beginPath()
    ctx.arc(pos.x, pos.y, 8, 0, Math.PI * 2)
    ctx.fill()
    return
  }

  ctx.fillStyle = color
  ctx.beginPath()
  ctx.arc(pos.x, pos.y, 16, 0, Math.PI * 2)
  ctx.fill()

  const labelText = `${routeIndex + 1}`
  const circleRadius = 20

  ctx.fillStyle = 'rgba(255, 255, 255, 0.95)'
  ctx.beginPath()
  ctx.arc(pos.x, pos.y, circleRadius, 0, Math.PI * 2)
  ctx.fill()

  ctx.strokeStyle = color
  ctx.lineWidth = 4
  ctx.beginPath()
  ctx.arc(pos.x, pos.y, circleRadius, 0, Math.PI * 2)
  ctx.stroke()

  ctx.fillStyle = color
  ctx.font = 'bold 22px sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(labelText, pos.x, pos.y)
}

interface GridBounds {
  minX: number
  maxX: number
  minY: number
  maxY: number
}

// グリッドを描画（bounds 指定時はその範囲、未指定なら原点±50m）
function drawGrid(
  ctx: CanvasRenderingContext2D,
  worldToCanvas: (x: number, y: number) => { x: number; y: number } | null,
  bounds?: GridBounds,
  gridSpacing: number = 1.0
): void {
  const defaultRange = 50
  const minX = bounds ? bounds.minX : -defaultRange
  const maxX = bounds ? bounds.maxX : defaultRange
  const minY = bounds ? bounds.minY : -defaultRange
  const maxY = bounds ? bounds.maxY : defaultRange

  const startX = Math.ceil(minX / gridSpacing) * gridSpacing
  const startY = Math.ceil(minY / gridSpacing) * gridSpacing

  ctx.strokeStyle = 'rgba(200, 200, 200, 0.8)'
  ctx.lineWidth = 1

  for (let x = startX; x <= maxX; x += gridSpacing) {
    const topPoint = worldToCanvas(x, maxY)
    const bottomPoint = worldToCanvas(x, minY)
    if (topPoint && bottomPoint) {
      ctx.beginPath()
      ctx.moveTo(topPoint.x, topPoint.y)
      ctx.lineTo(bottomPoint.x, bottomPoint.y)
      ctx.stroke()
    }
  }

  for (let y = startY; y <= maxY; y += gridSpacing) {
    const leftPoint = worldToCanvas(minX, y)
    const rightPoint = worldToCanvas(maxX, y)
    if (leftPoint && rightPoint) {
      ctx.beginPath()
      ctx.moveTo(leftPoint.x, leftPoint.y)
      ctx.lineTo(rightPoint.x, rightPoint.y)
      ctx.stroke()
    }
  }
}

// 経路（複数ポイント）を描画
function drawRoutePath(
  ctx: CanvasRenderingContext2D,
  points: { x: number; y: number }[],
  o: RouteDrawOptions,
  actionLabel?: string
): void {
  if (points.length < 2) return

  const startPos = points[0]
  const endPos = points[points.length - 1]

  const routeLineWidth = o.lineWidth ?? (o.isHighlighted ? 10 : 8)
  const routeOutlineWidth = 3
  const routeLineDash = o.isHighlighted ? [10, 5] : []
  ctx.setLineDash(routeLineDash)
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'

  const buildRoutePath = () => {
    ctx.beginPath()
    ctx.moveTo(points[0].x, points[0].y)
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y)
    }
  }

  ctx.strokeStyle = 'white'
  ctx.lineWidth = routeLineWidth + routeOutlineWidth * 2
  buildRoutePath()
  ctx.stroke()

  ctx.strokeStyle = o.color
  ctx.lineWidth = routeLineWidth
  buildRoutePath()
  ctx.stroke()
  ctx.setLineDash([])

  if (o.showIntermediatePoints ?? true) {
    for (let i = 1; i < points.length - 1; i++) {
      ctx.fillStyle = o.color
      ctx.strokeStyle = o.color
      ctx.lineWidth = 4
      ctx.beginPath()
      ctx.arc(points[i].x, points[i].y, 10, 0, Math.PI * 2)
      ctx.stroke()
    }
  }

  let totalRouteLength = 0
  for (let i = 1; i < points.length; i++) {
    const dx = points[i].x - points[i - 1].x
    const dy = points[i].y - points[i - 1].y
    totalRouteLength += Math.sqrt(dx * dx + dy * dy)
  }

  const minRouteLengthForArrow = 50
  if (
    points.length >= 2 &&
    (o.showArrow ?? true) &&
    totalRouteLength >= minRouteLengthForArrow
  ) {
    const lastSegmentStart = points[points.length - 2]
    const lastSegmentEnd = points[points.length - 1]
    const angle = Math.atan2(
      lastSegmentEnd.y - lastSegmentStart.y,
      lastSegmentEnd.x - lastSegmentStart.x
    )

    const arrowHeadSize = 20
    const arrowShaftWidth = 6
    const outlineWidth = 3
    const offsetFromEnd = 30

    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'

    const arrowTipX = endPos.x - offsetFromEnd * Math.cos(angle)
    const arrowTipY = endPos.y - offsetFromEnd * Math.sin(angle)
    const shaftEndX = arrowTipX
    const shaftEndY = arrowTipY

    const headBaseX = shaftEndX - arrowHeadSize * 0.5 * Math.cos(angle)
    const headBaseY = shaftEndY - arrowHeadSize * 0.5 * Math.sin(angle)
    const headLeftX = headBaseX - arrowHeadSize * Math.cos(angle - Math.PI / 4)
    const headLeftY = headBaseY - arrowHeadSize * Math.sin(angle - Math.PI / 4)
    const headRightX = headBaseX - arrowHeadSize * Math.cos(angle + Math.PI / 4)
    const headRightY = headBaseY - arrowHeadSize * Math.sin(angle + Math.PI / 4)

    ctx.strokeStyle = 'white'
    ctx.lineWidth = arrowShaftWidth + outlineWidth * 3
    ctx.beginPath()
    ctx.moveTo(shaftEndX, shaftEndY)
    ctx.lineTo(headLeftX, headLeftY)
    ctx.moveTo(shaftEndX, shaftEndY)
    ctx.lineTo(headRightX, headRightY)
    ctx.stroke()

    ctx.strokeStyle = o.color
    ctx.lineWidth = arrowShaftWidth
    ctx.beginPath()
    ctx.moveTo(shaftEndX, shaftEndY)
    ctx.lineTo(headLeftX, headLeftY)
    ctx.moveTo(shaftEndX, shaftEndY)
    ctx.lineTo(headRightX, headRightY)
    ctx.stroke()
  }

  // スタート/エンドポイント
  if (o.showSlots ?? true) {
    for (const pos of [startPos, endPos]) {
      if ((o.slotStyle ?? 'default') === 'red-circle') {
        ctx.fillStyle = 'red'
        ctx.beginPath()
        ctx.arc(pos.x, pos.y, 8, 0, Math.PI * 2)
        ctx.fill()
      } else {
        ctx.fillStyle = o.color
        ctx.beginPath()
        ctx.arc(pos.x, pos.y, o.isHighlighted ? 16 : 14, 0, Math.PI * 2)
        ctx.fill()
      }
    }
  }

  // ラベル（番号）- スタートポイント上に◯囲みで表示
  if ((o.showSlots ?? true) && (o.slotStyle ?? 'default') !== 'red-circle') {
    const labelText = `${o.displayIndex !== undefined ? o.displayIndex + 1 : o.routeIndex + 1}`
    const circleRadius = 20

    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)'
    ctx.beginPath()
    ctx.arc(startPos.x, startPos.y, circleRadius, 0, Math.PI * 2)
    ctx.fill()

    ctx.strokeStyle = o.color
    ctx.lineWidth = 4
    ctx.beginPath()
    ctx.arc(startPos.x, startPos.y, circleRadius, 0, Math.PI * 2)
    ctx.stroke()

    ctx.fillStyle = o.color
    ctx.font = 'bold 22px sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(labelText, startPos.x, startPos.y)
  }

  // アクション表示（END位置に。荷積み・荷下ろしのみ表示し、移動系は表示しない）
  if (
    o.showActionLabel &&
    (actionLabel === 'pickup' || actionLabel === 'place') &&
    ENABLE_CARGO_TOGGLE
  ) {
    const label = actionLabel === 'pickup' ? '荷積み' : '荷下ろし'

    ctx.font = 'bold 20px sans-serif'
    const textWidth = ctx.measureText(label).width

    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)'
    ctx.fillRect(
      endPos.x - textWidth / 2 - 8,
      endPos.y + 15,
      textWidth + 16,
      28
    )
    ctx.strokeStyle = o.color
    ctx.lineWidth = 3
    ctx.strokeRect(
      endPos.x - textWidth / 2 - 8,
      endPos.y + 15,
      textWidth + 16,
      28
    )

    ctx.fillStyle = o.color
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(label, endPos.x, endPos.y + 29)
  }

  // 荷ありルート: パスの中間点（経路長の半分の位置）に「荷」バッジを表示
  if (o.isLoaded) {
    const midPos = getPathMidPoint(points)

    // 白地に青縁・青文字の四角バッジ
    const badgeHalf = 14
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)'
    ctx.fillRect(
      midPos.x - badgeHalf,
      midPos.y - badgeHalf,
      badgeHalf * 2,
      badgeHalf * 2
    )
    ctx.strokeStyle = LOADED_BADGE_COLOR
    ctx.lineWidth = 3
    ctx.strokeRect(
      midPos.x - badgeHalf,
      midPos.y - badgeHalf,
      badgeHalf * 2,
      badgeHalf * 2
    )
    ctx.fillStyle = LOADED_BADGE_COLOR
    ctx.font = 'bold 18px sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('荷', midPos.x, midPos.y)
  }
}

export interface DrawRouteMapCanvasOptions {
  canvas: HTMLCanvasElement
  pose: Pose
  map: RouteMap | null
  routesToRender: RouteWithAction[]
  bgImage: HTMLImageElement | null
  imageLoaded: boolean
  scale: number
  offset: { x: number; y: number }
  canvasParams: MapCanvasParams
  baseCanvasWidthMeter: number
  showSlots: boolean
  showSlotLabels?: boolean
  visibleSlotIds?: Set<string>
  worldToCanvas: (x: number, y: number) => { x: number; y: number }
  getRouteColor: (routeIndex: number) => string
  isRouteHighlighted: (routeIndex: number) => boolean
  showRouteActionLabel: (routeIndex: number) => boolean
  getDisplayIndex: (routeIndex: number) => number
  showIntermediatePoints: boolean
  showRouteSlots: boolean
  showArrow: boolean
  lineWidth?: number
  slotStyle: 'default' | 'red-circle'
  showGrid: boolean
  /** 爪方向アニメーション（ルートに沿ってフォークリフト形状を走行表示）を行うか */
  animateForkDirection?: boolean
  /**
   * 爪方向アニメーションの走行ルート列（配列順に走行）。
   * 描画ルートとは別にシナリオ順で再生したい場合（参照モードで
   * マップ全ルートを表示しつつ選択シナリオの順に走らせる等）に指定する。
   * 未指定なら routesToRender を displayIndex 順に並べ替えて使う。
   */
  forkAnimRoutes?: RouteWithAction[]
  /** 爪方向アニメーションの再生開始時刻（epoch ms）。停止→再生で最初から再生するための位相基準 */
  forkAnimStartMs?: number
  /** 再生開始時にこのシナリオインデックスのルートを先に1回再生してから先頭ループに入る */
  forkAnimFirstRouteIndex?: number | null
  drawBackgroundImage: (
    ctx: CanvasRenderingContext2D,
    img: HTMLImageElement,
    center: { x: number; y: number },
    offset: { x: number; y: number },
    scale: number,
    canvasParams: MapCanvasParams,
    baseCanvasWidthMeter: number
  ) => void
  drawForklift: (
    ctx: CanvasRenderingContext2D,
    label: string,
    x: number,
    y: number,
    theta: number,
    color: string,
    size: number,
    bodyColor?: string,
    centerLabel?: string,
    strokeColor?: string,
    loaded?: boolean
  ) => void
}

// スロットマーカーを描画（三角形）
function drawSlots(
  ctx: CanvasRenderingContext2D,
  o: DrawRouteMapCanvasOptions,
  shapeSize: number
): void {
  if (!o.showSlots || !o.map) return

  for (const slot of o.map.slots.values()) {
    if (o.visibleSlotIds && !o.visibleSlotIds.has(slot.id)) continue

    const station = o.map.stations.get(slot.stationId)
    if (!station) continue

    const stationPos = o.worldToCanvas(station.pose.x, station.pose.y)
    const isCargo = ENABLE_CARGO_TOGGLE && slot.type === SLOT_TYPE_CARGO
    const slotColor = isCargo ? '#f97316' : 'pink'
    const slotStrokeColor = isCargo ? '#ea580c' : '#f9a8d4'
    const cargoLabel = isCargo ? '高' : undefined
    const slotLabel = (o.showSlotLabels ?? true) ? slot.id : ''
    o.drawForklift(
      ctx,
      slotLabel,
      stationPos.x,
      stationPos.y,
      station.pose.theta ?? 0,
      slotColor,
      shapeSize,
      slotColor,
      cargoLabel,
      slotStrokeColor
    )
  }
}

// ルートを描画（プレイバックシナリオモード）
function drawRoutes(
  ctx: CanvasRenderingContext2D,
  o: DrawRouteMapCanvasOptions
): void {
  if (!o.map) return

  // 停止手順の描画位置。自身の停止スロットが無い（シナリオ先頭の停止）場合は、
  // 次に走行するルートの始点（= 出発位置）に描く
  const routesInOrder: typeof o.routesToRender = []
  o.routesToRender.forEach((route, routeIndex) => {
    routesInOrder[o.getDisplayIndex(routeIndex)] = route
  })
  const resolveWaitSlotId = (routeIndex: number): string | undefined => {
    const route = o.routesToRender[routeIndex]
    const own = route.startSlot || route.slots[0]
    if (own) return own
    for (
      let i = o.getDisplayIndex(routeIndex) + 1;
      i < routesInOrder.length;
      i++
    ) {
      const next = routesInOrder[i]
      if (next && !isWaitStep(next)) return next.startSlot || next.slots[0]
    }
    return undefined
  }

  // 荷あり区間（荷積み後〜荷下ろしまで・荷物あり移動）を事前計算
  const loadedFlags = computeLoadedFlags(o.routesToRender)

  o.routesToRender.forEach((route, routeIndex) => {
    const color = o.getRouteColor(routeIndex)

    const startSlotId = route.startSlot || route.slots[0]
    const endSlotId = route.endSlot || route.slots[1] || route.slots[0]

    // 停止手順: 走行しないので線は描かず、停止位置に番号付きの「停止 n秒」ラベルだけ描く
    if (isWaitStep(route)) {
      const waitSlotId = resolveWaitSlotId(routeIndex)
      const slot = waitSlotId ? o.map!.slots.get(waitSlotId) : undefined
      const station = slot ? o.map!.stations.get(slot.stationId) : undefined
      if (!station?.pose || !o.showRouteActionLabel(routeIndex)) return
      const pos = o.worldToCanvas(station.pose.x, station.pose.y)
      if (!pos) return
      drawWaitStepLabel(ctx, pos, {
        displayIndex: o.getDisplayIndex(routeIndex),
        color,
        isHighlighted: o.isRouteHighlighted(routeIndex),
        label: isResumeWaitStep(route)
          ? 'タップで再開'
          : `停止 ${route.waitSec ?? 0}秒`,
      })
      return
    }

    const startSlot = o.map!.slots.get(startSlotId)
    const endSlot = o.map!.slots.get(endSlotId)
    if (!startSlot || !endSlot) return

    const routeStations = buildRoutePoints(o.map!, startSlotId, endSlotId)
    if (!routeStations) return

    if (routeStations.length === 1) {
      const station = routeStations[0]
      const pos = o.worldToCanvas(station.pose.x, station.pose.y)
      if (pos && o.showRouteSlots) {
        drawSinglePoint(ctx, pos, { routeIndex, color })
      }
      return
    }

    const points = routeStations
      .map((station) => o.worldToCanvas(station.pose.x, station.pose.y))
      .filter((p) => p !== null) as { x: number; y: number }[]
    if (points.length < 2) return

    drawRoutePath(
      ctx,
      points,
      {
        routeIndex,
        color,
        isHighlighted: o.isRouteHighlighted(routeIndex),
        showActionLabel: o.showRouteActionLabel(routeIndex),
        displayIndex: o.getDisplayIndex(routeIndex),
        showIntermediatePoints: o.showIntermediatePoints,
        showSlots: o.showRouteSlots,
        showArrow: o.showArrow,
        lineWidth: o.lineWidth,
        slotStyle: o.slotStyle,
        isLoaded: loadedFlags[routeIndex],
      },
      route.action
    )
  })
}

// 停止手順のラベルを描画（番号円 + 「停止 n秒」/「タップで再開」）。
// 直前ルートの終点円・アクションラベルと重ならないよう停止位置の右下に置く
function drawWaitStepLabel(
  ctx: CanvasRenderingContext2D,
  pos: { x: number; y: number },
  o: {
    displayIndex: number
    color: string
    isHighlighted?: boolean
    label: string
  }
): void {
  const label = o.label
  const circleRadius = 16
  const boxHeight = 32
  const left = pos.x + 24
  const top = pos.y + 24
  const lineWidth = o.isHighlighted ? 5 : 3

  ctx.setLineDash([])
  ctx.font = 'bold 20px sans-serif'
  const textWidth = ctx.measureText(label).width
  const boxLeft = left + circleRadius * 2 + 6
  const boxWidth = textWidth + 16

  // ラベル箱
  ctx.fillStyle = 'rgba(255, 255, 255, 0.95)'
  ctx.fillRect(boxLeft, top, boxWidth, boxHeight)
  ctx.strokeStyle = o.color
  ctx.lineWidth = lineWidth
  ctx.strokeRect(boxLeft, top, boxWidth, boxHeight)
  ctx.fillStyle = o.color
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(label, boxLeft + boxWidth / 2, top + boxHeight / 2)

  // 手順番号の円（ルートの始点円と同じ見え方）
  const cx = left + circleRadius
  const cy = top + boxHeight / 2
  ctx.fillStyle = 'white'
  ctx.beginPath()
  ctx.arc(cx, cy, circleRadius, 0, Math.PI * 2)
  ctx.fill()
  ctx.strokeStyle = o.color
  ctx.lineWidth = lineWidth
  ctx.beginPath()
  ctx.arc(cx, cy, circleRadius, 0, Math.PI * 2)
  ctx.stroke()
  ctx.fillStyle = o.color
  ctx.font = 'bold 18px sans-serif'
  ctx.fillText(String(o.displayIndex + 1), cx, cy)
}

/** 爪方向アニメーションの走行速度（m/s 相当） */
const FORK_ANIM_SPEED_MPS = 4.0
/** 爪方向アニメーションのルート境界での停止時間（ms） */
const FORK_ANIM_ROUTE_PAUSE_MS = 600
/** 爪方向アニメーションの1ルートあたり最小走行時間（ms） */
const FORK_ANIM_MIN_TRAVEL_MS = 300
/** 爪方向アニメーションのフォークリフト色 */
const FORK_ANIM_COLOR = '#9ca3af'

interface ForkAnimPose {
  x: number
  y: number
  theta: number
}

// 2角度の最短差分を-πからπの範囲で返す（±π境界跨ぎでも逆回りしない）。
function shortestAngleDiff(to: number, from: number): number {
  const diff = to - from
  return (((diff % (2 * Math.PI)) + 3 * Math.PI) % (2 * Math.PI)) - Math.PI
}

// ルートの station 列から爪方向アニメーション用のポーズ列を作る。
// theta が欠けている station は次のセグメント方向（末尾は直前の theta）で補う。
function buildForkAnimPoses(routeStations: Station[]): ForkAnimPose[] {
  const poses: ForkAnimPose[] = routeStations.map((s) => ({
    x: s.pose.x,
    y: s.pose.y,
    theta: s.pose.theta ?? NaN,
  }))
  for (let i = 0; i < poses.length; i++) {
    if (!Number.isNaN(poses[i].theta)) continue
    const next = poses[i + 1]
    if (next) {
      poses[i].theta = Math.atan2(next.y - poses[i].y, next.x - poses[i].x)
    } else {
      poses[i].theta = i > 0 ? poses[i - 1].theta : 0
    }
  }
  return poses
}

// ポーズ列上の走行距離 dist の位置・向きを補間して求める。
// 向きは記録時の theta を最短角度で補間するため、後進区間では
// 爪が進行方向と逆を向いたまま移動する（＝前進/後進が見た目で分かる）。
function interpolateForkAnimPose(
  poses: ForkAnimPose[],
  dist: number
): ForkAnimPose {
  let acc = 0
  for (let i = 1; i < poses.length; i++) {
    const p1 = poses[i - 1]
    const p2 = poses[i]
    const segLen = Math.hypot(p2.x - p1.x, p2.y - p1.y)
    if (acc + segLen >= dist && segLen > 0) {
      const t = (dist - acc) / segLen
      return {
        x: p1.x + (p2.x - p1.x) * t,
        y: p1.y + (p2.y - p1.y) * t,
        theta: p1.theta + shortestAngleDiff(p2.theta, p1.theta) * t,
      }
    }
    acc += segLen
  }
  return poses[poses.length - 1]
}

// 爪方向アニメーションを描画。
// シナリオ順にルートを連結し、記録時の theta を保ったままフォークリフト形状を
// ループ走行させることで、各ルートを前進（爪が進行方向）で走るのか
// 後進（爪が進行方向と逆）で走るのかを可視化する。
function drawForkDirectionAnimation(
  ctx: CanvasRenderingContext2D,
  o: DrawRouteMapCanvasOptions,
  shapeSize: number
): void {
  if (!o.animateForkDirection || !o.map) return

  // 走行ルート列。forkAnimRoutes 指定時はその配列順（シナリオ順）をそのまま使う。
  // 未指定時は routesToRender がハイライト対象を最後に描く並び替え入りのため、
  // displayIndex（シナリオ上の実インデックス）で走行順を復元する
  // 停止手順は走行しないためアニメーションからは除く
  const orderedRoutes = (
    o.forkAnimRoutes
      ? o.forkAnimRoutes.map((route, index) => ({ route, order: index }))
      : o.routesToRender
          .map((route, routeIndex) => ({
            route,
            order: o.getDisplayIndex(routeIndex),
          }))
          .sort((a, b) => a.order - b.order)
  ).filter(({ route }) => !isWaitStep(route))

  // 荷あり区間（荷積み後〜荷下ろしまで・荷物あり移動）は荷物を載せて描画する。
  // 走行中は computeLoadedFlags と同じ区間で判定し、ルート境界の停止中は
  // 荷役後の状態を反映する（荷積みルートの終点停止では荷あり、
  // 荷下ろしルートの終点停止では荷なし）
  const loadedFlags = computeLoadedFlags(orderedRoutes.map((r) => r.route))

  // 各ルートのポーズ列・経路長・所要時間を組み立てる
  const animRoutes: {
    poses: ForkAnimPose[]
    length: number
    travelMs: number
    order: number
    travelLoaded: boolean
    pauseLoaded: boolean
  }[] = []
  for (const [index, { route, order }] of orderedRoutes.entries()) {
    const startSlotId = route.startSlot || route.slots[0]
    const endSlotId = route.endSlot || route.slots[1] || route.slots[0]
    const routeStations = buildRoutePoints(o.map, startSlotId, endSlotId)
    if (!routeStations || routeStations.length === 0) continue
    const poses = buildForkAnimPoses(routeStations)
    let length = 0
    for (let i = 1; i < poses.length; i++) {
      length += Math.hypot(
        poses[i].x - poses[i - 1].x,
        poses[i].y - poses[i - 1].y
      )
    }
    const action = route.action ?? 'move'
    animRoutes.push({
      poses,
      length,
      travelMs: Math.max(
        (length / FORK_ANIM_SPEED_MPS) * 1000,
        FORK_ANIM_MIN_TRAVEL_MS
      ),
      order,
      travelLoaded: loadedFlags[index],
      pauseLoaded:
        action === 'pickup'
          ? true
          : action === 'place'
            ? false
            : loadedFlags[index],
    })
  }
  if (animRoutes.length === 0) return

  // 全ルート（走行＋境界停止）を1周期としてループ再生する
  const totalMs = animRoutes.reduce(
    (sum, r) => sum + r.travelMs + FORK_ANIM_ROUTE_PAUSE_MS,
    0
  )
  const elapsed = Date.now() - (o.forkAnimStartMs ?? 0)

  // 選択されたルートがあれば、まずそのルートだけを1回再生（イントロ）し、
  // その後に先頭からの通常ループへ移行する
  const firstRoute =
    o.forkAnimFirstRouteIndex != null
      ? animRoutes.find((r) => r.order === o.forkAnimFirstRouteIndex)
      : undefined
  let playRoutes = animRoutes
  let t: number
  if (firstRoute) {
    const introMs = firstRoute.travelMs + FORK_ANIM_ROUTE_PAUSE_MS
    if (elapsed < introMs) {
      playRoutes = [firstRoute]
      t = elapsed
    } else {
      t = (elapsed - introMs) % totalMs
    }
  } else {
    t = elapsed % totalMs
  }
  let current: ForkAnimPose | null = null
  let loaded = false
  for (const animRoute of playRoutes) {
    if (t < animRoute.travelMs) {
      const dist = (t / animRoute.travelMs) * animRoute.length
      current = interpolateForkAnimPose(animRoute.poses, dist)
      loaded = animRoute.travelLoaded
      break
    }
    t -= animRoute.travelMs
    if (t < FORK_ANIM_ROUTE_PAUSE_MS) {
      // ルート境界では終点で一時停止
      current = animRoute.poses[animRoute.poses.length - 1]
      loaded = animRoute.pauseLoaded
      break
    }
    t -= FORK_ANIM_ROUTE_PAUSE_MS
  }
  if (!current) return

  const pos = o.worldToCanvas(current.x, current.y)
  if (!pos) return

  ctx.save()
  ctx.globalAlpha = 0.9
  o.drawForklift(
    ctx,
    '',
    pos.x,
    pos.y,
    current.theta,
    FORK_ANIM_COLOR,
    shapeSize,
    '#4b5563',
    undefined,
    'white',
    loaded
  )
  ctx.restore()
}

export function drawRouteMapCanvas(o: DrawRouteMapCanvasOptions): void {
  const ctx = o.canvas.getContext('2d')
  if (!ctx) return

  if (o.canvas.width === 0 || o.canvas.height === 0) {
    console.warn('Canvas dimensions are 0, skipping draw')
    return
  }

  ctx.clearRect(0, 0, o.canvas.width, o.canvas.height)

  // 背景画像
  if (o.imageLoaded && o.bgImage) {
    o.drawBackgroundImage(
      ctx,
      o.bgImage,
      { x: o.pose.x, y: o.pose.y },
      o.offset,
      o.scale,
      o.canvasParams,
      o.baseCanvasWidthMeter
    )
  }

  // グリッド
  if (o.showGrid) {
    let gridBounds: GridBounds | undefined
    const ppm = o.canvasParams.imagePixelPerMeter
    if (o.imageLoaded && o.bgImage && ppm) {
      const { imageOriginPixX, imageOriginPixY } = o.canvasParams
      gridBounds = {
        minX: -imageOriginPixX / ppm,
        maxX: (o.bgImage.width - imageOriginPixX) / ppm,
        minY: (imageOriginPixY - o.bgImage.height) / ppm,
        maxY: imageOriginPixY / ppm,
      }
    }
    drawGrid(ctx, o.worldToCanvas, gridBounds)
  }

  const effectiveWidthMeter = o.baseCanvasWidthMeter / o.scale
  const pixelsPerMeter = o.canvas.width / effectiveWidthMeter
  const shapeSize = pixelsPerMeter

  drawSlots(ctx, o, shapeSize)
  drawRoutes(ctx, o)
  // ルート番号マーカー（半径20px）と重なっても埋もれにくい下限サイズ
  drawForkDirectionAnimation(ctx, o, Math.max(shapeSize, 40))
}

// ===============================
// ルート編集モードのマーク描画
// ===============================

// 編集マークの表示情報（theta は区間方向から算出済みの機体向き）
export interface EditMarkDisplay {
  x: number
  y: number
  theta: number
  isCargo: boolean
  reverse: boolean
}

// 編集マークのヒットテスト半径（キャンバス px、DPR 適用前）
export const EDIT_MARK_HIT_RADIUS = 22

// 回転ハンドルの配置（マーク中心から theta 方向への距離）と描画半径。
// 描画とヒットテストで同じ位置を使うため関数で共有する。
export function editRotateHandleGeometry(size: number): {
  dist: number
  radius: number
} {
  return { dist: size * 1.5, radius: Math.max(8, size * 0.15) }
}

// 編集中のマーク列を描画する。
// - マーク間ポリライン（オレンジ / 後進区間は破線）
// - 各マーク: フォークリフト型グリフ（theta 方向に爪が向く。荷役は「高」ラベル）+ 番号
// - 選択マーク: 黄色い縁取り + リング / エラーマーク: 赤い縁取り
// - rotateHandleIndex のマーク: 向き変更用の回転ハンドル（theta 方向の先の丸）
export function drawEditMarks(
  ctx: CanvasRenderingContext2D,
  worldToCanvas: (x: number, y: number) => { x: number; y: number },
  marks: EditMarkDisplay[],
  selectedIndex: number | null,
  errorIndices: Set<number>,
  size: number,
  drawForkliftFn: DrawRouteMapCanvasOptions['drawForklift'],
  rotateHandleIndex: number | null = null
): void {
  if (marks.length === 0) return
  const pts = marks.map((m) => worldToCanvas(m.x, m.y))
  const color = '#f97316'

  // 区間ポリライン（白縁 + オレンジ。後進区間は破線）
  for (let i = 1; i < pts.length; i++) {
    ctx.save()
    if (marks[i].reverse) ctx.setLineDash([10, 7])
    ctx.lineCap = 'round'
    ctx.beginPath()
    ctx.moveTo(pts[i - 1].x, pts[i - 1].y)
    ctx.lineTo(pts[i].x, pts[i].y)
    ctx.strokeStyle = 'white'
    ctx.lineWidth = 7
    ctx.stroke()
    ctx.beginPath()
    ctx.moveTo(pts[i - 1].x, pts[i - 1].y)
    ctx.lineTo(pts[i].x, pts[i].y)
    ctx.strokeStyle = errorIndices.has(i) ? '#dc2626' : color
    ctx.lineWidth = 3.5
    ctx.stroke()
    ctx.restore()
  }

  marks.forEach((mark, i) => {
    const p = pts[i]
    const isSelected = selectedIndex === i
    const hasError = errorIndices.has(i)

    // 選択マークのハイライト（グリフ背面の黄色いリング）
    if (isSelected) {
      const r = size * 0.95
      ctx.beginPath()
      ctx.arc(p.x, p.y, r, 0, Math.PI * 2)
      ctx.strokeStyle = '#facc15'
      ctx.lineWidth = 5
      ctx.stroke()
      ctx.beginPath()
      ctx.arc(p.x, p.y, r, 0, Math.PI * 2)
      ctx.strokeStyle = 'rgba(0,0,0,0.4)'
      ctx.lineWidth = 1.5
      ctx.stroke()
    }

    // フォークリフト型グリフ（爪が theta 方向 = 機体の向き。スロット描画と同じ形状）。
    // 色もスロット表示に合わせる（荷役 = オレンジ / 通常 = ピンク）。
    const strokeColor = isSelected ? '#facc15' : hasError ? '#dc2626' : 'white'
    drawForkliftFn(
      ctx,
      '',
      p.x,
      p.y,
      mark.theta,
      mark.isCargo ? '#f97316' : 'pink',
      size,
      '#4b5563',
      mark.isCargo ? '高' : undefined,
      strokeColor
    )

    // 向き変更用の回転ハンドル（theta 方向の先に丸を描き、ドラッグで回転）
    if (rotateHandleIndex === i) {
      const { dist, radius } = editRotateHandleGeometry(size)
      const hx = p.x + Math.cos(-mark.theta) * dist
      const hy = p.y + Math.sin(-mark.theta) * dist
      ctx.save()
      ctx.setLineDash([4, 4])
      ctx.beginPath()
      ctx.moveTo(p.x, p.y)
      ctx.lineTo(hx, hy)
      ctx.strokeStyle = '#facc15'
      ctx.lineWidth = 2
      ctx.stroke()
      ctx.restore()
      ctx.beginPath()
      ctx.arc(hx, hy, radius, 0, Math.PI * 2)
      ctx.fillStyle = 'white'
      ctx.fill()
      ctx.strokeStyle = '#facc15'
      ctx.lineWidth = 3
      ctx.stroke()
      ctx.beginPath()
      ctx.arc(hx, hy, radius, 0, Math.PI * 2)
      ctx.strokeStyle = 'rgba(0,0,0,0.4)'
      ctx.lineWidth = 1
      ctx.stroke()
    }

    // マーク番号（1始まり、グリフの右上）
    const label = `${i + 1}`
    const offset = size * 0.8
    ctx.font = 'bold 13px sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.strokeStyle = 'white'
    ctx.lineWidth = 3
    ctx.strokeText(label, p.x + offset, p.y - offset)
    ctx.fillStyle = '#374151'
    ctx.fillText(label, p.x + offset, p.y - offset)
  })
}
